/**
 * Timing and keyframes for the hover overflow reveal (the ChatGPT-sidebar behaviour): a label that
 * does not fit is clipped with a gradient fade instead of an ellipsis, and while its row is hovered
 * or focused the text slides left so its tail can be read, holds, and starts over.
 *
 * The shape is the one ChatGPT ships — a pause, a slide at a speed proportional to the type size, a
 * hold on the tail, looping forever, with the mask fading the leading edge in over the first quarter
 * of the slide and fading the trailing edge out over the last quarter. The three durations run
 * faster than ChatGPT's (350ms / two em per second / 1850ms): in a dense IDE panel a name is
 * something to glance at, not to watch.
 *
 * One deliberate difference: ChatGPT hangs its clip box `lead` pixels to the left of the text with a
 * negative margin, which the designer's menu rows would cut off (`.kuika-menu-item`,
 * `.kuika-flex-center--gap` and `__text` all clip). Here the clip box keeps its natural width and the
 * mask rectangle is shifted instead, which puts the same fades over the same pixels of visible text.
 */

/** Pause before the text starts sliding. */
export const OVERFLOW_REVEAL_LEAD_DELAY_MS: number = 250;

/** Pause on the tail of the text before the loop restarts. */
export const OVERFLOW_REVEAL_TAIL_HOLD_MS: number = 1200;

/** Slide speed, in em per second — so bigger text scrolls proportionally faster. */
export const OVERFLOW_REVEAL_SPEED_EM_PER_SECOND: number = 3;

/** Used when the computed font size cannot be parsed (jsdom returns an empty string). */
export const OVERFLOW_REVEAL_FALLBACK_FONT_SIZE_PX: number = 13;

export const OVERFLOW_REVEAL_SCROLL_EASING: string = "cubic-bezier(0.49, 0.6, 0.7, 1)";

/** Where inside the slide the leading fade finishes fading in / the trailing fade starts fading out. */
const MASK_FADE_IN_RATIO: number = 0.25;
const MASK_FADE_OUT_RATIO: number = 0.75;

export interface OverflowRevealTiming {
  /** Length of one loop: lead delay + slide + tail hold. */
  durationMs: number;
  /** Length of the sliding part alone. */
  scrollMs: number;
  /** Normalised offset at which the slide starts. */
  slideStartOffset: number;
  /** Normalised offset at which the slide ends. */
  slideEndOffset: number;
}

export interface OverflowRevealKeyframes {
  track: Keyframe[];
  mask: Keyframe[];
}

export const getOverflowRevealTiming = (overflowPx: number, fontSizePx: number): OverflowRevealTiming => {
  const resolvedFontSizePx: number =
    Number.isFinite(fontSizePx) && fontSizePx > 0 ? fontSizePx : OVERFLOW_REVEAL_FALLBACK_FONT_SIZE_PX;
  const pixelsPerSecond: number = resolvedFontSizePx * OVERFLOW_REVEAL_SPEED_EM_PER_SECOND;
  const scrollMs: number = (overflowPx / pixelsPerSecond) * 1000;
  const durationMs: number = OVERFLOW_REVEAL_LEAD_DELAY_MS + scrollMs + OVERFLOW_REVEAL_TAIL_HOLD_MS;

  return {
    durationMs,
    scrollMs,
    slideStartOffset: OVERFLOW_REVEAL_LEAD_DELAY_MS / durationMs,
    slideEndOffset: (OVERFLOW_REVEAL_LEAD_DELAY_MS + scrollMs) / durationMs
  };
};

interface MaskFrame {
  maskSize: string;
  maskPosition: string;
  WebkitMaskSize: string;
  WebkitMaskPosition: string;
}

/**
 * Mask rectangle per phase, in the clip box's own coordinates. The gradient itself never changes —
 * only which part of it covers the text.
 *
 * - at rest the rectangle starts `lead` px to the left of the box, so the leading fade falls outside
 *   it and the first character stays crisp while the tail fades;
 * - while sliding both edges fade, so characters enter and leave softly;
 * - at the end the rectangle is `trail` px wider than the box, pushing the trailing fade past the
 *   right edge so the last character is crisp.
 */
const MASK_AT_REST: MaskFrame = {
  maskSize: "calc(100% + var(--kuika-overflow-text-lead)) 100%",
  maskPosition: "calc(-1 * var(--kuika-overflow-text-lead)) 0",
  WebkitMaskSize: "calc(100% + var(--kuika-overflow-text-lead)) 100%",
  WebkitMaskPosition: "calc(-1 * var(--kuika-overflow-text-lead)) 0"
};

const MASK_WHILE_SLIDING: MaskFrame = {
  maskSize: "100% 100%",
  maskPosition: "0 0",
  WebkitMaskSize: "100% 100%",
  WebkitMaskPosition: "0 0"
};

const MASK_AT_END: MaskFrame = {
  maskSize: "calc(100% + var(--kuika-overflow-text-trail)) 100%",
  maskPosition: "0 0",
  WebkitMaskSize: "calc(100% + var(--kuika-overflow-text-trail)) 100%",
  WebkitMaskPosition: "0 0"
};

export const getOverflowRevealKeyframes = (
  overflowPx: number,
  timing: OverflowRevealTiming
): OverflowRevealKeyframes => {
  const slideTransform: string = `translateX(${-overflowPx}px)`;
  const slideSpan: number = timing.slideEndOffset - timing.slideStartOffset;

  return {
    track: [
      { transform: "translateX(0)", offset: 0 },
      { transform: "translateX(0)", offset: timing.slideStartOffset, easing: OVERFLOW_REVEAL_SCROLL_EASING },
      { transform: slideTransform, offset: timing.slideEndOffset },
      { transform: slideTransform, offset: 1 }
    ],
    mask: [
      { ...MASK_AT_REST, offset: 0 },
      { ...MASK_AT_REST, offset: timing.slideStartOffset },
      { ...MASK_WHILE_SLIDING, offset: timing.slideStartOffset + slideSpan * MASK_FADE_IN_RATIO },
      { ...MASK_WHILE_SLIDING, offset: timing.slideStartOffset + slideSpan * MASK_FADE_OUT_RATIO },
      { ...MASK_AT_END, offset: timing.slideEndOffset },
      { ...MASK_AT_END, offset: 1 }
    ]
  };
};
